import _ from 'lodash';
import constants from '#packages/constants';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as util from '#packages/util';
import * as panels from '#packages/panels';
import { translate } from '#packages/i18n';
import * as helpIds from '#packages/helpIds';
import * as leftBar from '#packages/leftBar';
import { connectWithScope } from '#packages/apilib';
import { ErrorReporter } from '@wix/editor-error-reporter';
import { addPanelDragToStage } from '#packages/addPanelInfra';
import { fedopsLogger } from '#packages/util';
import { CustomScroll } from '@wix/wix-base-ui';
import { cx } from '#packages/util';

import AddSectionThumbnail from './components/addSectionThumbnail';
import AddPresetHtmlPreview from '../previews/addPresetHtmlPreview';
import AddSectionRawHtmlPreview from '../previews/addSectionRawHtmlPreview';

import AddSectionCategories from './components/addSectionCategories';
import SavedSectionEmptyState from './components/savedSectionsEmtpyState';
import { mapStateToProps, mapDispatchToProps } from './addSectionPanelMapper';
import htmlThumbnailDragBox from '../htmlPreviewThumbnail/htmlThumbnailDragBox';
import AISectionCreator from './components/aiSectionCreator';

import styles from './addSectionPanel.scss';

import type { EditorInteractionName } from 'types/fedops';
import type {
  AddSectionPanelDispatchProps,
  AddSectionPanelOwnProps,
  AddSectionPanelStateProps,
} from './addSectionPanelMapper';
import { AddPresetScope } from '../addPresetApiEntry';
import type {
  SectionPresetDefinition,
  ExtraPreviewProps,
  PresetReadyEntry,
} from '../types';
import { ADD_SECTION_PANEL_CONTENT_WIDTH } from '../addPresetConsts';
import { CREATE_WITH_AI_CATEGORY_ID, SavedSectionCategory } from './consts';
import { AddSectionCategoryHeader } from './components/addSectionCategoryHeader';
import {
  AddSectionCategoryAdditionalInfo,
  type AddSectionCategoryAdditionalInfoProps,
} from './components/addSectionCategoryAdditionalInfo';
import experiment from 'experiment';

const { withDragToStage } = util.hoc;

interface AddSectionPanelProps
  extends AddSectionPanelStateProps,
    AddSectionPanelOwnProps,
    AddSectionPanelDispatchProps {}

let startLoadTime: number | undefined;
let firstReadyFired = false;

const setStartLoadTime = () => {
  startLoadTime = performance.now();
};

const AddSectionPanel: React.FC<AddSectionPanelProps> = ({
  language,
  panelName,
  categories,
  stageEntryIndex,
  startItemDrag,
  openHelpCenter,
  onSectionClick,
  onSectionDragEnd,
  onSectionAddFromContextMenu,
  currentSiteThemeMap,
  currentSiteFontsUrls,
  updatePanelOpenCount,
  initialSelectedCategory,
  isCreateSectionWithAIEnabled,
  sendCategoryLoadBiEvent,
  setPanelSelectedCategory,
  getBlankSectionStructure,
  sendContentScrolledEvent,
  addBlankSectionStructure,
  sendSectionsLoadedPhaseEvent,
  onClose,
  sendSectionsTooltipHoverEvent,
  loadSavedSections,
  savedSections,
  openPanelInteraction,
  loadSections,
  origin,
  getEnrichedSections,
}: AddSectionPanelProps) => {
  const isWixServicesAutoBindingEnabled = experiment.isOpen(
    'se_wixServicesAutoBindingCMS',
  );

  useEffect(() => {
    openPanelInteraction?.end();
    updatePanelOpenCount();
    ErrorReporter.breadcrumb('addSection panel opened');
    // mapDispatchToProps is run 2 times, so we can't add updatePanelOpenCount as a dependency here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [selectedCategoryIndex, setSelectedCategory] = useState<number>(
    initialSelectedCategory,
  );
  const [presetSections, setSections] = useState<SectionPresetDefinition[]>([]);
  const [presetsReady, setPresetsReady] = useState<PresetReadyEntry>({});
  const savedSectionSelected =
    categories[selectedCategoryIndex].id === SavedSectionCategory.id;
  const createWithAiSectionSelected =
    categories[selectedCategoryIndex].id === CREATE_WITH_AI_CATEGORY_ID;
  let sections = savedSectionSelected ? savedSections : presetSections;
  if (isWixServicesAutoBindingEnabled) {
    sections = getEnrichedSections(sections);
  }
  const [isSectionsAllReady, setIsSectionsAllReady] = useState(false);

  const tooltipHoverStartTime = useRef(undefined);
  const allPresetsLoadPromise = useRef<Promise<void>[]>(null);
  const presetsResolveMap = useRef(null);
  const sectionCategoryAdditionalInfoWrapperRef =
    useRef<HTMLInputElement | null>(null);
  const sectionCategoryAdditionalInfoHeight =
    sectionCategoryAdditionalInfoWrapperRef?.current?.clientHeight ?? 0;
  const heightRelativeToParentOfSectionPanelContent =
    sectionCategoryAdditionalInfoHeight
      ? `calc(100% - ${sectionCategoryAdditionalInfoHeight}px)`
      : '100%';

  const onceSendFirstPresetReadyEvent = () => {
    const endTime = performance.now();
    sendSectionsLoadedPhaseEvent(
      endTime - startLoadTime,
      categories[selectedCategoryIndex].title,
      'firstReady',
    );
    fedopsLogger.interactionEnded(
      fedopsLogger.INTERACTIONS.ADD_SECTION_PREVIEW_LOAD
        .ADD_SECTION_PREVIEWER_FIRST_READY,
      {
        customParams: {
          categoryId: categories[selectedCategoryIndex]?.id,
          categoryName: categories[selectedCategoryIndex]?.title,
        },
      },
    );
    firstReadyFired = true;
  };
  const debouncedScrollHandler = useMemo<
    (scrollTop: number, categoryTitle: string) => void
  >(
    () => _.debounce(sendContentScrolledEvent, 300),
    [sendContentScrolledEvent],
  );

  const hasSections = sections.length > 0;

  useEffect(() => {
    setIsSectionsAllReady(false);
    return () => {
      setPanelSelectedCategory(selectedCategoryIndex);
    };
  }, [selectedCategoryIndex, setPanelSelectedCategory]);

  const loadPresets = () => {
    sendCategoryLoadBiEvent(categories[selectedCategoryIndex].title);

    const categoryId = categories[selectedCategoryIndex].id;

    setPresetsReady({});

    if (createWithAiSectionSelected) return;

    const loadSectionsFn = () => {
      if (savedSectionSelected) return loadSavedSections();
      return loadSections(categoryId, language);
    };
    loadSectionsFn().then((sections) => {
      presetsResolveMap.current = {};
      allPresetsLoadPromise.current = sections.map(
        ({ _id }) =>
          new Promise<void>((resolve) => {
            presetsResolveMap.current[_id] = resolve;
          }),
      );

      setSections(
        isWixServicesAutoBindingEnabled
          ? getEnrichedSections(sections)
          : sections,
      );
      Promise.all(allPresetsLoadPromise.current).then(() => {
        setIsSectionsAllReady(true);
        const endTime = performance.now();
        sendSectionsLoadedPhaseEvent(
          endTime - startLoadTime,
          categories[selectedCategoryIndex].title,
          'allReady',
        );
        fedopsLogger.interactionEnded(
          fedopsLogger.INTERACTIONS.ADD_SECTION_PREVIEW_LOAD
            .ADD_SECTION_PREVIEWER_ALL_READY,
          {
            customParams: {
              categoryId: categories[selectedCategoryIndex]?.id,
              categoryName: categories[selectedCategoryIndex]?.title,
            },
          },
        );
      });
    });
  };

  useEffect(() => {
    util.fedopsLogger.interactionStarted(
      util.fedopsLogger.INTERACTIONS.ADD_SECTION_PREVIEW_LOAD
        .ADD_SECTION_PREVIEWER_FIRST_READY,
      {
        customParams: {
          categoryId: categories[selectedCategoryIndex]?.id,
          categoryName: categories[selectedCategoryIndex]?.title,
        },
      },
    );
    const interaction =
      util.fedopsLogger.INTERACTIONS.ADD_SECTION_PREVIEW_LOAD
        .ADD_SECTION_PREVIEWER_ALL_READY;
    util.fedopsLogger.interactionStarted(interaction, {
      customParams: {
        categoryId: categories[selectedCategoryIndex]?.id,
        categoryName: categories[selectedCategoryIndex]?.title,
      },
    });
    firstReadyFired = false;
    setStartLoadTime();
    loadPresets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategoryIndex]);

  useEffect(() => {
    setStartLoadTime();
    util.keyboardShortcuts.setContext(
      util.keyboardShortcuts.CONTEXTS.ADD_SECTION_PANEL,
    );
    return () => {
      util.keyboardShortcuts.setContext(util.keyboardShortcuts.CONTEXTS.EDITOR);
      onClose?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setPresetReady = (id: string, overridePresetReady?: boolean) => {
    if (!presetsReady[id] || overridePresetReady) {
      setPresetsReady((updatedPresetsReady) => ({
        ...updatedPresetsReady,
        [id]: true,
      }));
    }
  };

  const {
    tooltipDescription,
    tooltipTitle,
    longTitle,
    additionalCategoryInfo,
    name,
    isAppInstallRequired,
  } = categories[selectedCategoryIndex];

  const appName = additionalCategoryInfo?.appName || name;

  const onContentScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop } = e.target as HTMLDivElement;
    debouncedScrollHandler(scrollTop, categories[selectedCategoryIndex].title);
  };

  const handleTooltipOpen = () => {
    tooltipHoverStartTime.current = performance.now();
  };

  const handleTooltipClose = () => {
    if (tooltipHoverStartTime) {
      const endTime = performance.now();
      const duration = endTime - tooltipHoverStartTime.current;
      sendSectionsTooltipHoverEvent(
        duration,
        categories[selectedCategoryIndex].title,
      );
    }
    tooltipHoverStartTime.current = undefined;
  };

  const onPreviewReady = (
    sectionId: string,
    fedopsInteractionKey: EditorInteractionName,
    overridePresetReady?: boolean,
  ) => {
    if (!presetsReady[sectionId] || overridePresetReady) {
      setPresetReady(sectionId, overridePresetReady);
      if (!firstReadyFired) {
        onceSendFirstPresetReadyEvent();
      }
      presetsResolveMap?.current?.[sectionId]?.(); // TODO remove when saved section store ready
      fedopsLogger.interactionEnded(fedopsInteractionKey, {
        customParams: {
          presetId: sectionId,
          categoryId: categories[selectedCategoryIndex]?.id || '',
          categoryName: categories[selectedCategoryIndex]?.title || '',
        },
      });
    }
  };

  const getPreview = (
    sectionDefinition: SectionPresetDefinition,
    previewProps: ExtraPreviewProps,
  ) => {
    const sectionId = sectionDefinition._id;

    const isSavedSectionCategory =
      categories[selectedCategoryIndex].id === SavedSectionCategory.id;

    const fedopsSectionsInteractions =
      fedopsLogger.INTERACTIONS.ADD_SECTION_PREVIEW_LOAD;

    if (isSavedSectionCategory) {
      return (
        <AddSectionRawHtmlPreview
          {...previewProps}
          key={sectionId}
          sectionDefinition={sectionDefinition}
          stageEntryIndex={stageEntryIndex}
          onReady={() => {
            onPreviewReady(
              sectionId,
              fedopsSectionsInteractions.ADD_SECTION_RAW_HTML_PREVIEW_LOAD,
            );
          }}
        />
      );
    }

    return (
      <AddPresetHtmlPreview
        key={sectionId}
        previewHtmlUrl={sectionDefinition.previewHtmlUrl}
        fontsUrls={currentSiteFontsUrls}
        containerWidth={ADD_SECTION_PANEL_CONTENT_WIDTH}
        currentSiteThemeMap={currentSiteThemeMap}
        themeMap={sectionDefinition.themeMap}
        onReady={() => {
          onPreviewReady(
            sectionId,
            fedopsSectionsInteractions.ADD_SECTION_HTML_PREVIEW_LOAD,
          );
        }}
        height={previewProps.height}
        interactionKey={
          fedopsSectionsInteractions.ADD_SECTION_HTML_PREVIEW_LOAD
        }
      />
    );
  };

  const getAddSectionThumbnail = (
    sectionDefinition: SectionPresetDefinition,
  ) => {
    const appIdForSectionPreset =
      isWixServicesAutoBindingEnabled &&
      sectionDefinition?.additionalPresetInfo?.appId;
    const isAppInstallRequiredBySection = !!appIdForSectionPreset;
    const appNameToInstall = appIdForSectionPreset
      ? sectionDefinition?.additionalPresetInfo?.appName
      : appName;

    return (
      <AddSectionThumbnail
        onSectionDragEnd={onSectionDragEnd}
        getBlankSectionStructure={getBlankSectionStructure}
        onSectionClick={onSectionClick}
        onSectionAddFromContextMenu={onSectionAddFromContextMenu}
        startItemDrag={startItemDrag}
        key={sectionDefinition._id}
        sectionDefinition={sectionDefinition}
        categoryDefinition={categories[selectedCategoryIndex]}
        stageEntryIndex={stageEntryIndex}
        isAppInstallRequired={
          isAppInstallRequired || isAppInstallRequiredBySection
        }
        appName={appNameToInstall}
        isPresetReady={presetsReady?.[sectionDefinition._id]}
        renderPreview={(previewProps) =>
          getPreview(sectionDefinition, previewProps)
        }
      />
    );
  };

  const addSectionCategoryAdditionalInfo = () => {
    const addSectionCategoryAdditionalInfoProps: AddSectionCategoryAdditionalInfoProps =
      {
        appName,
        appIcon: additionalCategoryInfo.appIcon,
        description: additionalCategoryInfo.description,
      };

    return (
      <div
        className={styles.sectionCategoryAdditionalInfoWrapper}
        ref={sectionCategoryAdditionalInfoWrapperRef}
      >
        <AddSectionCategoryAdditionalInfo
          {...addSectionCategoryAdditionalInfoProps}
        />
      </div>
    );
  };

  const addSectionPanelContent = () => {
    const className = cx(styles.addSectionPanelContent, {
      [styles.addSectionPanelContent_topIndent]: isAppInstallRequired,
      'add-section-panel-content-all-ready': isSectionsAllReady,
    });

    return (
      <div
        data-aid="add-section-panel-content"
        className={className}
        style={{ width: ADD_SECTION_PANEL_CONTENT_WIDTH }}
      >
        {createWithAiSectionSelected
          ? addSectionCreatedWithAI()
          : addSectionPanelDefaultContent()}
      </div>
    );
  };

  const addSectionCreatedWithAI = () => (
    <AISectionCreator
      onSectionDragEnd={onSectionDragEnd}
      onSectionClick={onSectionClick}
      onSectionAddFromContextMenu={onSectionAddFromContextMenu}
      startItemDrag={startItemDrag}
      stageEntryIndex={stageEntryIndex}
      origin={origin}
    />
  );

  const addSectionPanelDefaultContent = () => (
    <>
      {hasSections && !isAppInstallRequired && (
        <AddSectionCategoryHeader
          longTitle={longTitle}
          tooltipTitle={tooltipTitle}
          tooltipDescription={tooltipDescription}
          linkText={categories[selectedCategoryIndex]?.linkText}
          handleTooltipOpen={handleTooltipOpen}
          handleTooltipClose={handleTooltipClose}
          onLinkClick={() =>
            openHelpCenter(categories[selectedCategoryIndex]?.helpId, null, {
              origin: 'saved_section_tooltip',
            })
          }
        />
      )}

      {!hasSections && savedSectionSelected && (
        <SavedSectionEmptyState openHelpCenter={openHelpCenter} />
      )}

      {sections.map((sectionDefinition) =>
        getAddSectionThumbnail(sectionDefinition),
      )}
    </>
  );

  const LeftPanelFrame = util.workspace.isNewWorkspaceEnabled()
    ? leftBar.LeftPanelFrame
    : panels.frames.LeftPanelFrame;

  return (
    <LeftPanelFrame
      panelName={panelName}
      panelIndex={2}
      panelClass="add-section-panel"
      automationId="add-section-panel"
      className={styles.addSectionPanel}
      label={translate('section_add_section_panel_title')}
      onHelpClicked={() =>
        openHelpCenter(helpIds.addSectionPanel.PANEL_HELP_BUTTON, null, {
          origin: 'saved_section_tooltip',
        })
      }
    >
      <AddSectionCategories
        categories={categories}
        isCreateSectionWithAIEnabled={isCreateSectionWithAIEnabled}
        selectedCategory={selectedCategoryIndex}
        onCategoryChanged={setSelectedCategory}
        addBlankSectionStructure={addBlankSectionStructure}
      />
      <div
        className={cx(
          styles.addSectionPanelContentWrapper,
          styles.addSectionPanelContentWrapper_noScroll,
        )}
      >
        {isAppInstallRequired && addSectionCategoryAdditionalInfo()}
        <CustomScroll
          onScroll={onContentScroll}
          heightRelativeToParent={heightRelativeToParentOfSectionPanelContent}
        >
          {addSectionPanelContent()}
        </CustomScroll>
      </div>
    </LeftPanelFrame>
  );
};

export default connectWithScope(
  () => AddPresetScope,
  withDragToStage<AddSectionPanelProps>(
    {
      ...addPanelDragToStage,
      type: constants.MOUSE_ACTION_TYPES.ADD_SECTION_PANEL_DRAG_TO_STAGE,
    },
    htmlThumbnailDragBox,
  )(AddSectionPanel),
  mapStateToProps,
  mapDispatchToProps,
);
